// ─── Math Core Logic & Helpers ──────────────────────────────────────────────

export function gcd(a, b) {
  while (b !== 0) {
    [a, b] = [b, a % b]
  }
  return a
}

export function lcm(a, b) {
  return (a * b) / gcd(a, b)
}

export function gcdMany(nums) {
  return nums.reduce(gcd)
}

export function lcmMany(nums) {
  return nums.reduce(lcm)
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const ROUND_DURATION_MS = 60_000 // 1 minute (60 seconds)
export const QUESTION_POOL_SIZE = 150   // Pool of questions for 1-minute speed run
export const CORRECT_POINTS = 2         // +2 points per correct answer
export const WRONG_POINTS = -1          // -1 point per wrong answer
export const FEEDBACK_DELAY_MS = 400    // 400ms rapid transition feedback delay

// ─── Internal Helpers ───────────────────────────────────────────────────────

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// ─── Quantitative Difficulty Validator ─────────────────────────────────────
// Rules:
// ค.ร.น. Easy <= 20, Medium <= 100, Hard <= 300
// ห.ร.ม. Easy max_num <= 20, Medium max_num <= 50, Hard max_num <= 100

export function isValidQuestion(numbers, type, difficulty) {
  if (!numbers || numbers.length < 2) return false
  const maxNum = Math.max(...numbers)

  if (type === 'hrm') {
    if (difficulty === 'easy') return maxNum <= 20
    if (difficulty === 'medium') return maxNum <= 50
    if (difficulty === 'hard') return maxNum <= 100
  } else {
    const ansLcm = lcmMany(numbers)
    if (difficulty === 'easy') return ansLcm <= 20
    if (difficulty === 'medium') return ansLcm <= 100
    if (difficulty === 'hard') return ansLcm <= 300
  }
  return true
}

// ─── Curated Number Pools ───────────────────────────────────────────────────

const PAIRS_EASY = [
  [2, 3], [2, 4], [2, 5], [2, 6], [2, 7], [2, 8], [2, 9], [2, 10], [2, 12], [2, 14], [2, 16], [2, 18], [2, 20],
  [3, 4], [3, 5], [3, 6], [3, 9], [3, 12], [3, 15], [3, 18],
  [4, 5], [4, 6], [4, 8], [4, 10], [4, 12], [4, 16], [4, 20],
  [5, 10], [5, 15], [5, 20], [6, 8], [6, 9], [6, 10], [6, 12], [6, 18],
  [8, 10], [8, 12], [8, 16], [9, 12], [9, 15], [9, 18], [10, 12], [10, 15], [10, 20], [12, 15], [12, 18], [14, 20],
]

const TRIPLES_EASY = [
  [2, 4, 6], [2, 4, 8], [2, 6, 8], [3, 6, 9], [3, 6, 12], [4, 6, 8], [4, 8, 12], [5, 10, 15], [5, 10, 20], [6, 9, 12],
]

const PAIRS_MEDIUM = [
  [8, 12], [12, 16], [12, 18], [12, 20], [12, 28], [12, 30], [12, 36], [14, 21], [14, 28], [14, 35], [14, 42],
  [15, 20], [15, 25], [15, 30], [15, 35], [15, 40], [15, 45], [16, 20], [16, 24], [16, 28], [16, 32], [16, 40],
  [18, 24], [18, 27], [18, 30], [18, 36], [18, 42], [18, 45], [20, 24], [20, 25], [20, 30], [20, 35], [20, 40], [20, 45], [20, 50],
  [21, 28], [21, 35], [21, 42], [24, 30], [24, 32], [24, 36], [24, 40], [25, 30], [25, 35], [25, 40], [25, 50],
  [27, 36], [27, 45], [28, 35], [28, 42], [30, 40], [30, 42], [30, 45], [30, 50], [32, 40], [32, 48], [35, 42], [36, 45], [36, 48],
]

const TRIPLES_MEDIUM = [
  [6, 9, 12], [6, 10, 15], [8, 12, 16], [8, 12, 20], [9, 12, 15], [9, 12, 18], [10, 15, 20], [10, 15, 25],
  [12, 15, 18], [12, 16, 20], [12, 18, 24], [14, 21, 28], [15, 20, 25], [15, 20, 30], [16, 24, 32], [18, 24, 30], [20, 25, 30],
]

const PAIRS_HARD = [
  [24, 36], [24, 40], [24, 48], [24, 60], [24, 72], [28, 42], [28, 56], [28, 70], [28, 84], [30, 45], [30, 60], [30, 75], [30, 90],
  [32, 48], [32, 64], [32, 80], [32, 96], [35, 70], [36, 48], [36, 54], [36, 60], [36, 72], [36, 84], [36, 90],
  [40, 50], [40, 60], [40, 75], [40, 80], [40, 100], [42, 56], [42, 63], [42, 70], [42, 84], [45, 60], [45, 75], [45, 90],
  [48, 60], [48, 64], [48, 72], [48, 80], [48, 96], [50, 75], [50, 100], [54, 72], [54, 81], [54, 90], [56, 70], [56, 84], [56, 96],
  [60, 75], [60, 80], [60, 90], [60, 100], [63, 84], [64, 80], [64, 96], [70, 105], [72, 90], [72, 96], [75, 100],
]

const TRIPLES_HARD = [
  [12, 18, 24], [12, 24, 36], [14, 21, 28], [15, 30, 45], [16, 24, 32], [18, 27, 36], [20, 30, 40], [21, 42, 63],
  [24, 36, 48], [24, 36, 60], [28, 42, 56], [28, 42, 70], [30, 45, 60], [32, 48, 64], [36, 48, 72], [40, 60, 80], [45, 60, 75], [48, 72, 96],
]

// ─── High-Entropy Procedural Generator (With Strict Bound Checking) ────────

function generateDynamicNumbers(difficulty, type, isTriple) {
  let attempts = 0
  let nums = []

  do {
    const strategy = randInt(1, 3)

    if (type === 'krn') {
      // ── ค.ร.น. Generation ──
      // Easy: LCM <= 20
      // Medium: LCM <= 100
      // Hard: LCM <= 300

      if (strategy === 1) {
        const k = randInt(1, difficulty === 'easy' ? 4 : difficulty === 'hard' ? 12 : 8)
        const p = randInt(1, difficulty === 'easy' ? 4 : difficulty === 'hard' ? 8 : 5)
        let q = randInt(1, difficulty === 'easy' ? 4 : difficulty === 'hard' ? 8 : 5)
        while (q === p) q = randInt(1, difficulty === 'easy' ? 4 : difficulty === 'hard' ? 8 : 5)

        if (!isTriple) {
          nums = [k * p, k * q].sort((a, b) => a - b)
        } else {
          let r = randInt(1, difficulty === 'easy' ? 4 : difficulty === 'hard' ? 8 : 5)
          while (r === p || r === q) r = randInt(1, difficulty === 'easy' ? 4 : difficulty === 'hard' ? 8 : 5)
          nums = [k * p, k * q, k * r].sort((a, b) => a - b)
        }
      } else {
        const maxVal = difficulty === 'easy' ? 20 : difficulty === 'hard' ? 150 : 60
        const a = randInt(2, maxVal)
        const b = randInt(2, maxVal)
        if (a !== b) {
          if (!isTriple) {
            nums = [a, b].sort((a, b) => a - b)
          } else {
            const c = randInt(2, maxVal)
            if (c !== a && c !== b) {
              nums = [a, b, c].sort((a, b) => a - b)
            }
          }
        }
      }
    } else {
      // ── ห.ร.ม. Generation ──
      // Easy: Max number <= 20
      // Medium: Max number <= 50
      // Hard: Max number <= 100
      const maxNumCap = difficulty === 'easy' ? 20 : difficulty === 'hard' ? 100 : 50

      if (strategy === 1) {
        const k = randInt(2, difficulty === 'easy' ? 5 : difficulty === 'hard' ? 20 : 10)
        const p = randInt(1, difficulty === 'easy' ? 3 : difficulty === 'hard' ? 5 : 4)
        let q = randInt(1, difficulty === 'easy' ? 3 : difficulty === 'hard' ? 5 : 4)
        while (q === p) q = randInt(1, difficulty === 'easy' ? 3 : difficulty === 'hard' ? 5 : 4)

        if (!isTriple) {
          nums = [k * p, k * q].sort((a, b) => a - b)
        } else {
          let r = randInt(1, difficulty === 'easy' ? 3 : difficulty === 'hard' ? 5 : 4)
          while (r === p || r === q) r = randInt(1, difficulty === 'easy' ? 3 : difficulty === 'hard' ? 5 : 4)
          nums = [k * p, k * q, k * r].sort((a, b) => a - b)
        }
      } else {
        const a = randInt(2, maxNumCap)
        const b = randInt(2, maxNumCap)
        if (a !== b && gcd(a, b) > 1) {
          if (!isTriple) {
            nums = [a, b].sort((a, b) => a - b)
          } else {
            const c = randInt(2, maxNumCap)
            if (c !== a && c !== b && gcdMany([a, b, c]) > 1) {
              nums = [a, b, c].sort((a, b) => a - b)
            }
          }
        }
      }
    }

    attempts++
  } while (
    (nums.length < (isTriple ? 3 : 2) ||
      new Set(nums).size < nums.length ||
      !isValidQuestion(nums, type, difficulty)) &&
    attempts < 60
  )

  if (!isValidQuestion(nums, type, difficulty) || new Set(nums).size < nums.length) {
    if (type === 'krn') {
      nums = difficulty === 'easy' ? (isTriple ? [2, 4, 6] : [3, 4])
           : difficulty === 'hard' ? (isTriple ? [12, 18, 24] : [24, 36])
           : (isTriple ? [6, 8, 12] : [12, 15])
    } else {
      nums = difficulty === 'easy' ? (isTriple ? [4, 8, 12] : [6, 9])
           : difficulty === 'hard' ? (isTriple ? [24, 36, 48] : [36, 48])
           : (isTriple ? [12, 18, 24] : [16, 24])
    }
  }

  return nums
}

// ─── Smart Distractors Generator ────────────────────────────────────────────

function generateChoices(answer, type, numbers) {
  const pool = new Set([answer])
  const candidates = []

  const a = numbers[0]
  const b = numbers[1]
  const c = numbers[2] ?? 0

  if (type === 'hrm') {
    // ── ห.ร.ม. Distractors ──
    for (let d = 1; d < answer; d++) {
      if (numbers.every(n => n % d === 0)) candidates.push(d)
    }

    const currentLcm = c ? lcmMany(numbers) : lcm(a, b)
    candidates.push(currentLcm)

    const diff = Math.abs(a - b)
    if (diff > 0 && diff !== answer) candidates.push(diff)
    candidates.push(Math.min(...numbers))

    if (answer * 2 < currentLcm) candidates.push(answer * 2)
    if (answer * 3 < currentLcm) candidates.push(answer * 3)

  } else {
    // ── ค.ร.น. Distractors ──
    const directProduct = c ? a * b * c : a * b
    if (directProduct !== answer) candidates.push(directProduct)

    const currentGcd = c ? gcdMany(numbers) : gcd(a, b)
    if (currentGcd !== answer) candidates.push(currentGcd)

    candidates.push(answer * 2)
    candidates.push(answer * 3)

    const maxVal = Math.max(...numbers)
    if (maxVal !== answer) candidates.push(maxVal)
    candidates.push(maxVal * 2)
  }

  // Fallback nearby values
  for (let δ = 1; δ <= 20; δ++) {
    if (answer - δ > 0) candidates.push(answer - δ)
    candidates.push(answer + δ)
  }

  const validCandidates = shuffle([...new Set(candidates)]).filter(c => c > 0 && c !== answer)

  for (const c of validCandidates) {
    if (pool.size >= 4) break
    pool.add(c)
  }

  for (let i = 1; pool.size < 4; i++) {
    if (!pool.has(i)) pool.add(i)
  }

  return shuffle([...pool])
}

// ─── Public API ─────────────────────────────────────────────────────────────

export function generateQuestions(count = QUESTION_POOL_SIZE, topicMode = 'mix', difficulty = 'medium') {
  const pairsPool = difficulty === 'easy' ? PAIRS_EASY
    : difficulty === 'hard' ? PAIRS_HARD
    : PAIRS_MEDIUM

  const triplesPool = difficulty === 'easy' ? TRIPLES_EASY
    : difficulty === 'hard' ? TRIPLES_HARD
    : TRIPLES_MEDIUM

  const shuffledPairs = shuffle(pairsPool)
  const shuffledTriples = shuffle(triplesPool)

  const usedSignatures = new Set()

  return Array.from({ length: count }, (_, i) => {
    let type
    if (topicMode === 'hrm') {
      type = 'hrm'
    } else if (topicMode === 'krn') {
      type = 'krn'
    } else {
      type = i % 2 === 0 ? 'hrm' : 'krn'
    }

    const useTriple = i % 4 === 3 // Every 4th question is triple numbers

    let numbers
    let attempts = 0
    do {
      if (Math.random() < 0.5 && attempts < 5) {
        const pool = useTriple ? shuffledTriples : shuffledPairs
        const candidate = pool[randInt(0, pool.length - 1)]
        if (isValidQuestion(candidate, type, difficulty)) {
          numbers = candidate
        }
      }

      if (!numbers) {
        numbers = generateDynamicNumbers(difficulty, type, useTriple)
      }
      attempts++
    } while (usedSignatures.has(`${type}_${numbers.join('_')}`) && attempts < 30)

    usedSignatures.add(`${type}_${numbers.join('_')}`)

    const answer = type === 'hrm' ? gcdMany(numbers) : lcmMany(numbers)

    return {
      id: i + 1,
      type,
      numbers,
      answer,
      choices: generateChoices(answer, type, numbers),
    }
  })
}

// ─── High Score & Rank Evaluation ──────────────────────────────────────────

export function getHighScore(mode = '1p') {
  try {
    const key = mode === '2p' ? 'lcm_war_highscore_2p_60s' : 'lcm_war_highscore_1p_60s'
    const saved = localStorage.getItem(key)
    return saved ? parseInt(saved, 10) : 0
  } catch (e) {
    return 0
  }
}

export function saveHighScore(mode = '1p', score = 0) {
  try {
    const key = mode === '2p' ? 'lcm_war_highscore_2p_60s' : 'lcm_war_highscore_1p_60s'
    const current = getHighScore(mode)
    if (score > current) {
      localStorage.setItem(key, score.toString())
      return true
    }
  } catch (e) {
    // Ignore storage errors
  }
  return false
}

export function getRankTitle(score) {
  if (score >= 60) return { title: '👑 เทพเจ้าคณิตศาสตร์ (Math Deity)', color: '#f59e0b' }
  if (score >= 40) return { title: '⚔️ เซียน ห.ร.ม./ค.ร.น. (GCD/LCM Master)', color: '#a78bfa' }
  if (score >= 20) return { title: '⚡ จอมพลังคำนวณ (Fast Calculator)', color: '#06b6d4' }
  return { title: '🌱 นักเรียนฝึกหัด (Math Learner)', color: '#10b981' }
}

// ─── UI Helpers ─────────────────────────────────────────────────────────────

export function typeLabel(type) {
  return type === 'hrm' ? 'ห.ร.ม.' : 'ค.ร.น.'
}

export function typeColor(type) {
  return type === 'hrm' ? '#a78bfa' : '#06b6d4'
}

export function typeBg(type) {
  return type === 'hrm'
    ? 'rgba(139,92,246,0.15)'
    : 'rgba(6,182,212,0.15)'
}
